import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../components/Input/Input";
import Loader from "../../../components/Loader/Loader";
import Notification from "../../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPut } from "../../../apiService";

/* ===================== STYLES ===================== */
import styles from "./EditAdminUser.module.scss";

const INITIAL_FORM = {
  name: "",
  mobile: "",
  designation: "",
  roleId: "",
};

const EditAdminUser = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const backPath = isAdminRoute ? "/admin/users-management" : "/users";

  const [form, setForm] = useState(INITIAL_FORM);
  const [userDetail, setUserDetail] = useState(null);
  const [editableFields, setEditableFields] = useState([]);
  const [roles, setRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const isEditable = (field) => editableFields.includes(field);

  const fetchRoles = useCallback(async (type) => {
    if (!type) return;
    try {
      const res = await apiGet("user", `/role?type=${type}`);
      setRoles(res?.data || []);
    } catch (error) {
      console.error("Failed to fetch roles", error);
    }
  }, []);

  const fetchUserDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("user", `/users/${id}`);
      if (res?.status === "success") {
        const data = res.data || {};
        setUserDetail(data);
        setEditableFields(data.editable || []);
        setForm({
          name: data.name || "",
          mobile: data.mobile || "",
          designation: data.designation || "",
          roleId: data.roleId || "",
        });
        fetchRoles(data.userType);
      } else {
        showNotification(res?.message || "Failed to load user", "error");
      }
    } catch (error) {
      console.error("Failed to load user detail", error);
      showNotification("Failed to load user detail", "error");
    } finally {
      setLoading(false);
    }
  }, [fetchRoles, id]);

  useEffect(() => {
    fetchUserDetail();
  }, [fetchUserDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!isEditable(name)) return;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (isEditable("name") && !form.name.trim()) {
      nextErrors.name = "Name is required";
    }
    if (isEditable("mobile") && !String(form.mobile).trim()) {
      nextErrors.mobile = "Mobile number is required";
    }
    if (isEditable("roleId") && !form.roleId) {
      nextErrors.roleId = "Role is required";
    }
    return nextErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        mobile: Number(form.mobile),
        designation: form.designation,
        roleId: Number(form.roleId),
      };

      const res = await apiPut("user", `/users/${id}`, payload);
      if (res?.status === "success") {
        showNotification("User updated successfully", "success");
        setTimeout(() => navigate(backPath), 1200);
      } else {
        showNotification(res?.message || "Failed to update user", "error");
      }
    } catch (error) {
      try {
        const parsed = JSON.parse(error.message);
        showNotification(parsed?.message || "Failed to update user", "error");
      } catch {
        showNotification("Failed to update user", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className={styles["edit-admin-user__page"]}>
      {notification && (
        <div className={styles["edit-admin-user__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      <div className={styles["edit-admin-user__header"]}>
        <Link to={backPath} className={styles["edit-admin-user__back-btn"]}>
          <ArrowLeft size={20} strokeWidth={2.5} />
        </Link>
        <div className={styles["edit-admin-user__title-group"]}>
          <h2 className={styles["edit-admin-user__title"]}>Edit User</h2>
          <p className={styles["edit-admin-user__subtitle"]}>
            Update user profile and role details.
          </p>
        </div>
      </div>

      <div className={styles["edit-admin-user__card"]}>
        <div className={styles["edit-admin-user__meta"]}>
          <div>
            <span>Email</span>
            <strong>{userDetail?.email || "--"}</strong>
          </div>
          <div>
            <span>Organization</span>
            <strong>{userDetail?.companyName || "--"}</strong>
          </div>
          <div>
            <span>Status</span>
            <strong>{userDetail?.status || "--"}</strong>
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className={styles["edit-admin-user__grid"]}>
            <Input
              label="Name"
              name="name"
              placeholder="Enter full name"
              value={form.name}
              onChange={handleChange}
              error={errors.name}
              disabled={!isEditable("name")}
              required={isEditable("name")}
            />

            <Input
              label="Mobile"
              type="number"
              name="mobile"
              placeholder="Enter mobile number"
              value={form.mobile}
              onChange={handleChange}
              error={errors.mobile}
              disabled={!isEditable("mobile")}
              required={isEditable("mobile")}
            />

            <Input
              label="Designation"
              name="designation"
              placeholder="Enter designation"
              value={form.designation}
              onChange={handleChange}
              disabled={!isEditable("designation")}
              required={isEditable("designation")}
            />

            <Input
              label="Role"
              type="select"
              name="roleId"
              placeholder="Select role"
              value={form.roleId}
              onChange={handleChange}
              error={errors.roleId}
              disabled={!isEditable("roleId")}
              required={isEditable("roleId")}
              options={roles.map((role) => ({
                label: role.name,
                value: role.id,
              }))}
            />
          </div>

          <div className={styles["edit-admin-user__actions"]}>
            <button
              type="button"
              className={`${styles["edit-admin-user__btn"]} ${styles["edit-admin-user__btn--secondary"]}`}
              onClick={() => navigate(backPath)}
              disabled={submitting}
            >
              <X size={16} strokeWidth={2.5} />
              Cancel
            </button>

            <button
              type="submit"
              className={`${styles["edit-admin-user__btn"]} ${styles["edit-admin-user__btn--primary"]}`}
              disabled={submitting}
            >
              <Save size={16} strokeWidth={2.5} />
              {submitting ? "Updating User..." : "Update User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAdminUser;
